import { $date } from '@/services/datetime';
import { copy } from '@/helpers/utilities';

export const defaultImageIndex = (id) => {
    return (id ? id % 6 : Math.floor(Math.random() * 6)) + 1;
};

export const defaultImage = (course) => {
    const index = course.default_cover_image || (course.id % 6) + 1;
    if (!course || isNaN(index)) return;
    return `/assets/course/Default_Course_Image_${index}.svg`;
};

export const coverImage = (course) => {
    if (!course) return;
    if (course.cover_image) return course.cover_image;
    if (course.coverImage) return course.coverImage;
    if (course.image) return course.image;
    return defaultImage(course);
};

export const cardImage = (course) => {
    if (!course) return;
    if (course.card_image) return course.card_image;
    if (course.cardImage) return course.cardImage;
    return defaultImage(course);
};

export const prerequisitesMet = (course, isShareLink) => {
    if (isShareLink) return true;
    return (course.prerequisites || []).every(p => p.completed || p.progress === 100);
};

export const availableIn = course => {
    if (!course.startDate) {
        return null;
    }
    const now = $date();
    const startDate = $date(course.startdate);
    return !startDate.isBefore(now) && startDate.diff(now, 'days');
};

export const expiresIn = course => {
    if (!course.enddate) {
        return null;
    }
    const endDate = $date(course.enddate);
    const now = $date();
    if (!endDate || endDate.isBefore(now)) {
        return null;
    }
    return endDate.diff(now, 'days');
};

export const isExpired = course => {
    if (!course.enddate) {
        return false;
    }
    const now = $date();
    return $date(course.enddate).isBefore(now);
};

export const getProgress = course => {
    if (availableIn(course) || !prerequisitesMet(course)) {
        return 'locked';
    }
    if ((course.progressPercent === 100) && course.gradingInProgress) {
        return 'grade_pending';
    }
    if ((course.progressPercent === 100) && (!course.completed)) {
        return 'incomplete';
    }
    if (course.completed) {
        return 'complete';
    }
    if (course.progressPercent) {
        return 'commenced';
    }
    return 'uncommenced';
};

export const startScreen = (course) => {
    if (!course) return {};
    const startOptions = {
        'overlay': 0.6,
        'display_button': true,
        'display_description': true,
        'display_duration': true,
        'display_pattern': true,
        'text_alignment': 'center',
        'button_label': 'Start course',
        'button_style': 'solid',
        'mode': 'inline-full',
        'layout': 'background',
        ...course.start
    };
    return copy({
        sequence: 0,
        title: course.title,
        description: course.description,
        asset: course.image,
        type: {
            id: 10,
            name: 'finish',
            completable: 0
        },
        object: {
            start: true,
            finish: false,
            legacy: false,
            ...startOptions
        }
    });
};

export const finishScreen = (course, isPublic = false, isHeadless = false) => {
    if (!course) return {};
    return copy({
        sequence: course.screens.length + 1,
        title: course.finish.title || 'You’re all done!',
        description: course.finish.description || 'How was your course experience?',
        asset: course.finish.asset,
        type: {
            id: 10,
            name: 'finish',
            completable: 0
        },
        object: {
            finish: true,
            legacy: false,
            'overlay': course.finish.overlay === null || course.finish.overlay === undefined ? 0.6 : course.finish.overlay,
            'text_alignment': course.finish.text_alignment || (course.finish.object && course.finish.object.text_alignment) || 'center',
            'bg_colour': course.finish.bg_colour || (course.finish.object && course.finish.object.bg_colour) || null,
            'display_title': course.finish.display_title === undefined ? true : course.finish.display_title,
            'display_description': course.finish.display_description === undefined ? true : course.finish.display_description,
            'display_button': course.finish.display_button === undefined ? !isHeadless : course.finish.display_button,
            'display_pattern': course.finish.display_pattern === undefined ? true : course.finish.display_pattern,
            'display_feedback': course.finish.display_feedback === undefined ? true : course.finish.display_feedback,
            'display_confetti': course.finish.display_confetti === undefined ? true : course.finish.display_confetti,
            'drag_index': course.finish.drag_index || 2,
            'image_mode': course.finish.image_mode || 'fill',
            'confetti_style': course.finish.confetti_style || 'fireworks',
            'button_label': course.finish.button_label || (isHeadless ? 'Next' : 'Create your own course'),
            'button_style': course.finish.button_style || 'solid',
            'link_url': course.finish.link_url || (isPublic || isHeadless ? null : 'https://coassemble.com/build'),
            'link_type': course.finish.link_type || 'custom',
            'layout': course.finish.layout || 'background',
            'mode': course.finish.mode || 'inline-full',
            'title_colour': course.finish.title_colour || null
        }
    });
};
